import type { CardPreviewProps } from "@/components/shapes/card-previews";
import {
  PreviewBody,
  PreviewTitle,
} from "@/components/shapes/shared/card-preview-parts";
import { SurfaceFade } from "@/components/ui";
import { useT } from "@/i18n";
import { framesConversationMessages } from "@/i18n/messages/frames-conversation";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import { markdownToPlainText } from "@/lib/markdown-plain-text";
import {
  useConversationStore,
  type ConversationMessage,
} from "@/stores/conversation-store";
import { useEqualSelector } from "@/hooks/use-equal-selector";
import {
  isMessageVisibleInConversationFrame,
  metadataString,
} from "./conversation-frame-utils";

/** The newest messages a chat card shows. */
const SHOWN_MESSAGES = 2;

/** The newest user and assistant messages this frame shows, oldest first. */
function lastMessages(
  messages: readonly ConversationMessage[],
  frame: { id: string; conversationId?: string },
): ConversationMessage[] {
  const shown: ConversationMessage[] = [];
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (!message || message.role === "system" || message.activityKind) continue;
    if (!message.content.trim()) continue;
    if (!isMessageVisibleInConversationFrame(message, frame)) continue;
    shown.unshift(message);
    if (shown.length === SHOWN_MESSAGES) break;
  }
  return shown;
}

function sameMessages(
  left: readonly ConversationMessage[],
  right: readonly ConversationMessage[],
): boolean {
  return (
    left.length === right.length &&
    left.every((message, index) => message === right[index])
  );
}

/**
 * A chat (mockup `chat`, Proposed): its title, then its newest messages,
 * each with the speaker above the text; no badge and no bubbles (two muted
 * lines before one is loaded). No thread or composer mounts.
 */
export function ConversationCardPreview({ frame, density }: CardPreviewProps) {
  const t = useT(framesConversationMessages);
  const conversationId = metadataString(
    frame.metadata ?? {},
    "conversationId",
    "conversation_id",
  );
  const messages = useConversationStore(
    useEqualSelector(
      (state: { messages: readonly ConversationMessage[] }) =>
        lastMessages(state.messages, { id: frame.frameId, conversationId }),
      sameMessages,
    ),
  );
  return (
    <PreviewBody
      density={density}
      cap="max-h-[17em]"
      className="relative gap-0"
    >
      <PreviewTitle>{frameDisplayTitle(frame)}</PreviewTitle>
      {messages.length > 0 ? (
        messages.map((message) => (
          <div
            key={message.id}
            className="mt-[0.9em] leading-[1.55] text-card-body first-of-type:mt-0"
            data-testid="card-preview-message"
          >
            <p className="mb-[0.2em] text-[0.78em] font-medium tracking-[0.05em] text-card-muted">
              {message.role === "user" ? t("you") : "Denker"}
            </p>
            <p className="line-clamp-3">
              {markdownToPlainText(message.content)}
            </p>
          </div>
        ))
      ) : (
        <span aria-hidden="true" className="grid gap-[0.6em] pt-[0.2em]">
          <i className="h-[0.55em] w-3/4 rounded-full bg-card-muted opacity-40" />
          <i className="h-[0.55em] w-1/2 rounded-full bg-card-muted opacity-40" />
        </span>
      )}
      {/* A long exchange fades at the card's cut, into its paper. */}
      <SurfaceFade at={density === "compact" ? undefined : "17em"} />
    </PreviewBody>
  );
}
